import { batch, bind, Component, h, signal, tag } from "omi";
import { throttle } from "lodash-es";

import { NWButton } from "@/wc/components/atoms/nw-button";
import { Timeout } from "@/types/timers.ts";
import { tailwind } from "@/tailwind";

import { isBooleanQuestion, Option, Question, QuestionStatus } from "../../models";
import type { QuizStore } from "../../store";
import { getShortcut } from "../../utils.ts";
import { AnswerTile } from "../answerTile";
import { ReasonSection } from "./reasonSection.tsx";

type Injection = {
  quizStore: QuizStore;
};

type Props = {
  isFinalQuestion: boolean;
  question: Question;
  checkAnswer: (question: Question, option: Option) => void;
  goToNextQuestion: (callback?: () => void) => void;
};

@tag("nw-quiz-question")
export class NWQuizQuestion extends Component<Props> {
  static css = [
    tailwind,
    `:host {
    outline: none;
  }`,
  ];

  inject = ["quizStore"];

  static propTypes = {
    question: Object,
    checkAnswer: Function,
  };

  private selectedOption = signal<Option | undefined>(undefined);
  private clickedOption = signal<Option | undefined>(undefined);
  private selectedOptionIndex = signal<number | undefined>(undefined);
  private clickedOptionIndex = signal<number | undefined>(undefined);
  private throttledHandleKeyDown: ReturnType<typeof throttle> | undefined;
  private focusTimeout: Timeout | undefined;

  installed(): void {
    this.addKeyboardListener();

    this.focusTimeout = setTimeout(() => {
      (this.rootElement as HTMLElement)?.focus();
    }, 0);
  }

  uninstall(): void {
    this.removeKeyboardListener();
    clearTimeout(this.focusTimeout);
  }

  private addKeyboardListener(): void {
    if (!this.throttledHandleKeyDown) {
      this.throttledHandleKeyDown = throttle(this.handleKeyDown, 20);
      document.addEventListener("keydown", this.throttledHandleKeyDown);
    }
  }

  private removeKeyboardListener(): void {
    if (this.throttledHandleKeyDown) {
      document.removeEventListener("keydown", this.throttledHandleKeyDown);
      this.throttledHandleKeyDown.cancel();
      this.throttledHandleKeyDown = undefined;
    }
  }

  @bind
  private handleKeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    const options = this.props.question.options;

    // Map keys A-D to indices 0-3
    const keyToIndex: Record<string, number> = {
      a: 0,
      b: 1,
      c: 2,
      d: 3,
    };

    const key = event.key.toLowerCase();
    const wasShortcut = key in keyToIndex;

    if (wasShortcut) {
      const index = keyToIndex[key];

      // Only select if the option exists at this index
      if (index >= 0 && index < options.length) {
        this.selectedOption.value = options[index];
        this.selectedOptionIndex.value = index;
      }
    }

    if (key === "enter" && this.selectedOption.value != null) {
      this.handleCheckAnswer();
    }
  }

  @bind
  private handleCheckAnswer(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    const hasAnswered = this.props.question.status !== QuestionStatus.NotStarted;

    if (hasAnswered) {
      this.props.goToNextQuestion(() => {
        batch(() => {
          this.selectedOption.value = undefined;
          this.selectedOptionIndex.value = undefined;
          this.clickedOption.value = undefined;
          this.clickedOptionIndex.value = undefined;
        });
      });
      return;
    }

    const question = this.props.question;
    const option = this.selectedOption.value;

    if (option == null) {
      return;
    }

    this.props.checkAnswer(question, option);
  }

  @bind
  private getOptionStatus(option: Option) {
    const question = this.props.question;
    const hasCheckedAnswer = question.status !== QuestionStatus.NotStarted;

    if (!hasCheckedAnswer) {
      return undefined;
    }

    const _option = option;
    const selectedOption = this.selectedOption.value;

    if (selectedOption == null) {
      return undefined;
    }

    return selectedOption.id === _option.id
      ? _option.isAnswer
        ? QuestionStatus.Correct
        : QuestionStatus.Incorrect
      : undefined;
  }

  private handleOptionClick(option: Option) {
    if (this.props.question.status !== QuestionStatus.NotStarted && this.selectedOption.value != null) {
      this.clickedOption.value = option;
      this.clickedOptionIndex.value = this.getOptionIndex(option);
      return;
    }

    this.selectedOption.value = option;
    this.selectedOptionIndex.value = this.getOptionIndex(option);
  }

  private getOptionIndex(option: Option) {
    const question = this.props.question;

    if (option == null) {
      return undefined;
    }

    return question.options.findIndex(o => option.id === o.id);
  }

  private renderOptions() {
    const question = this.props.question;
    const options = question.options;
    const selectedOption = this.selectedOption.value;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {options.map((option, index) => (
          <AnswerTile
            text={isBooleanQuestion(question) ? (option.value ? "True" : "False") : (option.value as string)}
            key={index}
            onClick={() => this.handleOptionClick(option)}
            selected={selectedOption?.id === option.id}
            status={this.getOptionStatus(option)}
          />
        ))}
      </div>
    );
  }

  render({ question, isFinalQuestion }: Props) {
    if (question == null) {
      return null;
    }

    const { quizStore } = this.injection as Injection;
    const isSubmitting = quizStore?.completeQuizMutation.signal.isLoading.value;
    const hasAnswered = question.status !== QuestionStatus.NotStarted;

    return (
      <div
        className="flex flex-col gap-6 focus:outline-none"
        tabIndex={0}
        onFocus={() => this.addKeyboardListener()}
        onBlur={() => this.removeKeyboardListener()}
      >
        <h2 className="text-primary text-2xl text-center">{question.question}</h2>

        {this.renderOptions()}

        {hasAnswered && (
          <div className="animate-fadeInFast">
            <ReasonSection
              option={this.clickedOption.value ?? this.selectedOption.value}
              key={getShortcut(this.clickedOptionIndex.value ?? this.selectedOptionIndex.value ?? 0)}
            />
          </div>
        )}

        <div className="flex justify-center">
          <NWButton
            type="primary"
            size="full"
            class="w-full max-w-56 pt-2"
            onClick={this.handleCheckAnswer}
            disabled={this.selectedOption.value == null}
            loading={isSubmitting}
          >
            {getCtaText(isFinalQuestion, hasAnswered)}
          </NWButton>
        </div>
      </div>
    );
  }
}

function getCtaText(isFinalQuestion: boolean, hasAnswered: boolean) {
  if (!hasAnswered) {
    return "Check answer";
  }

  return isFinalQuestion ? "Complete quiz" : "Next question";
}
